import {
  activityRows,
  analyse,
  daysAgo,
  fmtNum,
  lei,
  rfqState,
  roDate,
  type ActivityRow,
  t,
} from '@procumi/shared';
import { useMemo, useState } from 'react';
import { Meter } from '../components/Meter';
import { SiteDialog } from '../components/SiteDialog';
import { Panel } from '../components/Panel';
import {
  Documents,
  Donut,
  Ico,
  OfferCompare,
  rfqSteps,
  SiteArt,
  Stepper,
  SupplierStatus,
  Tiles,
  Timeline,
} from '../components/SiteBoard';
import { LedgerMaterials, LedgerOrders } from '../components/SiteLedger';
import { ErrorBox, Loading } from '../components/StateBox';
import { useAsync } from '../hooks/useAsync';
import type { Toast } from '../hooks/useToast';
import { api, errMsg } from '../lib/api';
import type { Route } from '../routes';
import { ask } from '../lib/confirm';

export function SiteDetailPage({ id, go, toast }: { id: string; go: (r: Route) => void; toast: Toast }) {
  const { data, error, loading, reload } = useAsync(() => api.site(id), [id], `site:${id}`);
  const [editing, setEditing] = useState(false);
  const [busy, setBusy] = useState(false);
  const [activeId, setActiveId] = useState('');
  const now = useMemo(() => Date.now(), [data]);
  // Cererea în lucru: întâi cea cu cele mai multe oferte, apoi cea cu termenul cel mai aproape.
  const openFull = useMemo(
    () =>
      [...(data?.open ?? [])].sort(
        (a, b) => analyse(b).cols.length - analyse(a).cols.length || a.deadline.localeCompare(b.deadline),
      ),
    [data],
  );

  const back = (
    <button className="back" onClick={() => go({ view: 'sites' })}>
      {t('‹ Toate șantierele')}
    </button>
  );

  if (loading) {
    return (
      <>
        {back}
        <Loading />
      </>
    );
  }
  if (error || !data) {
    return (
      <>
        {back}
        <ErrorBox error={error ?? t('Șantierul nu a fost găsit.')} onRetry={reload} />
      </>
    );
  }

  const { site, rfqs, ledger, events, documents, last } = data;
  const open = rfqs.filter((r) => rfqState(r) === 'open');
  const closed = rfqs.filter((r) => rfqState(r) === 'closed');
  const canceled = rfqs.filter((r) => rfqState(r) === 'canceled');

  const toggleClosed = async () => {
    setBusy(true);
    try {
      if (site.closedAt) {
        await api.reopenSite(site.id);
        toast(t('Șantier redeschis'));
      } else {
        await api.closeSite(site.id);
        toast(t('Șantier închis'));
      }
      reload();
    } catch (e) {
      toast(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    const ok = await ask({
      title: t('Ștergi șantierul?'),
      message: t('„{0}” nu are nicio cerere, deci nu se pierde nimic.', site.name),
      confirm: t('Șterge șantierul'),
      danger: true,
    });
    if (!ok) return;
    setBusy(true);
    try {
      await api.deleteSite(site.id);
      toast(t('Șantier șters'));
      go({ view: 'sites' });
    } catch (e) {
      toast(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  const active = openFull.find((r) => r.id === activeId) ?? openFull[0] ?? null;
  // fără cereri deschise: ultima încheiată, ca să se vadă cum s-a hotărât
  const shown = active ?? last;
  const used = ledger.ordered + ledger.planned;
  const gotOffers = openFull.reduce((n, r) => n + r.sends.filter((s) => s.offer).length, 0);
  const asked = openFull.reduce((n, r) => n + r.sends.length, 0);
  const openActivity = (r: ActivityRow) =>
    r.orderId
      ? go({ view: 'order', rfqId: r.rfqId!, orderId: r.orderId })
      : r.rfqId
        ? go({ view: 'rfq', id: r.rfqId })
        : undefined;

  return (
    <div className="pc sb">
      {back}
      <section className="sheet sb-hero pc-in">
        <SiteArt closed={!!site.closedAt} />
        <div className="sb-hero-t">
          <h1>
            {site.name}
            <span className={`sb-status${site.closedAt ? ' off' : ''}`}>
              {site.closedAt ? t('Închis') : t('Activ')}
            </span>
          </h1>
          <div className="sb-meta">
            {site.locality ? (
              <span>
                <Ico name="pin" size={14} /> {site.locality}
              </span>
            ) : null}
            {site.address ? (
              <span>
                <Ico name="truck" size={14} /> {site.address}
              </span>
            ) : null}
            {site.client ? (
              <span>
                <Ico name="user" size={14} /> {site.client}
              </span>
            ) : null}
          </div>
          <p className="sb-desc">
            {t(
              '{0} {1} de ofertă · {2} {3} · {4} {5}',
              site.rfqCount,
              site.rfqCount === 1 ? t('cerere') : t('cereri'),
              ledger.orderedCount,
              ledger.orderedCount === 1 ? t('comandă') : t('comenzi'),
              site.offerCount,
              site.offerCount === 1 ? t('ofertă primită') : t('oferte primite'),
            )}
          </p>
          <div className="sb-acts">
            {!site.closedAt ? (
              <button className="btn primary" onClick={() => go({ view: 'new', siteId: site.id })}>
                {t('Cerere nouă')}
              </button>
            ) : null}
            <button className="btn" onClick={() => setEditing(true)}>
              {t('Editează')}
            </button>
            <button className="btn" disabled={busy} onClick={() => void toggleClosed()}>
              {site.closedAt ? t('Redeschide') : t('Închide șantierul')}
            </button>
            {site.rfqCount === 0 ? (
              <button className="btn" disabled={busy} onClick={() => void remove()}>
                {t('Șterge')}
              </button>
            ) : null}
          </div>
        </div>
        {shown ? (
          <div className="sb-hero-s">
            <span className="sb-hero-k">{active ? t('Cererea în lucru') : t('Ultima cerere încheiată')}</span>
            <button className="sb-hero-r" onClick={() => go({ view: 'rfq', id: shown.id })}>
              {shown.title}
            </button>
            <Stepper steps={rfqSteps(shown)} />
          </div>
        ) : null}
      </section>

      <Tiles
        items={[
          {
            key: 'budget',
            icon: 'wallet',
            tone: 'blue',
            label: t('Buget de achiziții'),
            value: site.budget,
            empty: t('fără buget'),
            format: lei,
            sub: site.budget ? (
              used > site.budget ? (
                <span className="over">{t('peste buget cu {0}', lei(used - site.budget))}</span>
              ) : (
                t('folosit {0}% · rămân {1}', Math.round((used / site.budget) * 100), lei(site.budget - used))
              )
            ) : (
              <button className="link" onClick={() => setEditing(true)}>
                {t('Trece bugetul')}
              </button>
            ),
            meter: site.budget ? used / site.budget : undefined,
          },
          {
            key: 'ordered',
            icon: 'box',
            tone: 'night',
            label: t('Comandat'),
            value: ledger.ordered,
            format: lei,
            sub: `${ledger.orderedCount} ${ledger.orderedCount === 1 ? t('comandă') : t('comenzi')}${
              ledger.plannedCount ? t(' · urmează {0}', lei(ledger.planned)) : ''
            }`,
          },
          {
            key: 'gained',
            icon: 'piggy',
            tone: 'green',
            label: t('Economisit'),
            value: ledger.gained,
            format: lei,
            sub: ledger.gainedCount
              ? t(
                  'la {0} {1}, față de a doua ofertă',
                  ledger.gainedCount,
                  ledger.gainedCount === 1 ? t('comandă') : t('comenzi'),
                )
              : t('după prima comandă cu două oferte'),
          },
          {
            key: 'offers',
            icon: 'clock',
            tone: 'orange',
            label: t('Oferte primite'),
            value: asked ? gotOffers : null,
            empty: '—',
            format: (n) => `${n} / ${asked}`,
            sub: openFull.length
              ? `pe ${openFull.length} ${openFull.length === 1 ? t('cerere deschisă') : t('cereri deschise')}`
              : t('nicio cerere deschisă'),
            meter: asked ? gotOffers / asked : undefined,
          },
        ]}
      />

      <div className="pc-grid">
        <div className="pc-col">
          <Panel
            className="pc-in"
            title={t('Comparație oferte')}
            hint={shown ? (active ? shown.title : t('încheiată · {0}', shown.title)) : undefined}
            action={
              openFull.length > 1 ? (
                <select
                  className="inp sb-pick"
                  aria-label={t('Cererea comparată')}
                  value={active?.id ?? ''}
                  onChange={(e) => setActiveId(e.target.value)}
                >
                  {openFull.map((r) => (
                    <option key={r.id} value={r.id}>
                      {r.title}
                    </option>
                  ))}
                </select>
              ) : null
            }
          >
            {shown ? (
              <OfferCompare rfq={shown} onOpen={() => go({ view: 'rfq', id: shown.id })} />
            ) : (
              <div className="empty" style={{ padding: 'var(--space-5)' }}>
                {t(
                  'Nicio cerere deschisă pe acest șantier.{0}',
                  site.closedAt ? '' : t(' Apasă „Cerere nouă” ca să primești oferte.'),
                )}
              </div>
            )}
          </Panel>

          <LedgerOrders
            orders={ledger.orders}
            onOpenRfq={(rfqId) => go({ view: 'rfq', id: rfqId })}
            onOpenOrder={(rfqId, orderId) => go({ view: 'order', rfqId, orderId })}
          />

          <section className="sheet pc-in">
            <div className="pad" style={{ paddingBottom: 8 }}>
              <div className="section-title">
                <h2>{t('Cereri de ofertă')}</h2>
                <span className="muted" style={{ fontSize: '.9rem' }}>
                  {t('{0} deschise din {1}', open.length, rfqs.length)}
                </span>
              </div>
            </div>
            <div className="rfqs">
              {open.length ? (
                open.map((r) => {
                  const got = r.sends.filter((s) => s.hasOffer).length;
                  return (
                    <button className="rfq-row" key={r.id} onClick={() => go({ view: 'rfq', id: r.id })}>
                      <span>
                        <strong>{r.title}</strong>
                        <span className="sub">
                          <span className="pill cat">{r.category}</span>
                          {t('termen {0} · trimisă {1}', roDate(r.deadline), daysAgo(r.createdAt))}
                        </span>
                      </span>
                      <span className="meterbox">
                        <Meter sends={r.sends} />
                      </span>
                      <span className="count">
                        <b style={{ color: 'var(--ink)' }}>{t('{0} din {1}', got, r.sends.length)}</b>
                        <br />
                        {t('oferte')}
                      </span>
                    </button>
                  );
                })
              ) : (
                <div className="empty">
                  {t(
                    'Nicio cerere deschisă pe acest șantier.{0}',
                    site.closedAt ? '' : t(' Apasă „Cerere nouă".'),
                  )}
                </div>
              )}
            </div>
          </section>
        </div>

        <div className="pc-col">
          <Panel className="pc-in" title={t('Distribuția achizițiilor')}>
            <Donut byCategory={ledger.byCategory} bySupplier={ledger.bySupplier} />
          </Panel>
          {shown ? (
            <Panel className="pc-in" title={t('Furnizori și status')} hint={`${shown.sends.length}`}>
              <SupplierStatus rfq={shown} />
            </Panel>
          ) : null}
          <Panel className="pc-in" title={t('Activitate recentă')}>
            <Timeline rows={activityRows(events, 8)} now={now} onOpen={openActivity} />
          </Panel>
        </div>
      </div>

      <Panel
        className="pc-in"
        title={t('Documente și oferte')}
        hint={documents.length ? String(documents.length) : undefined}
      >
        <Documents docs={documents} />
      </Panel>

      {ledger.materials.length ? <LedgerMaterials ledger={ledger} toast={toast} /> : null}

      {canceled.length ? (
        <section className="sheet" style={{ marginTop: 18 }}>
          <div className="pad" style={{ paddingBottom: 8 }}>
            <div className="section-title">
              <h2>{t('Anulate')}</h2>
              <span className="muted" style={{ fontSize: '.9rem' }}>
                {canceled.length}
              </span>
            </div>
          </div>
          <div className="rfqs">
            {canceled.map((r) => (
              <button className="rfq-row" key={r.id} onClick={() => go({ view: 'rfq', id: r.id })}>
                <span>
                  <strong>{r.title}</strong>
                  <span className="sub">
                    <span className="pill cat">{r.category}</span> {t('anulată {0}', daysAgo(r.canceledAt!))}
                  </span>
                </span>
                <span className="count">
                  <span className="muted">{t('fără comandă')}</span>
                </span>
              </button>
            ))}
          </div>
        </section>
      ) : null}

      {closed.length ? (
        <section className="sheet" style={{ marginTop: 18 }}>
          <div className="pad" style={{ paddingBottom: 8 }}>
            <div className="section-title">
              <h2>{t('Închise')}</h2>
              <span className="muted" style={{ fontSize: '.9rem' }}>
                {closed.length}
              </span>
            </div>
          </div>
          <div className="rfqs">
            {closed.map((r) => (
              <button className="rfq-row" key={r.id} onClick={() => go({ view: 'rfq', id: r.id })}>
                <span>
                  <strong>{r.title}</strong>
                  <span className="sub">
                    <span className="pill cat">{r.category}</span>
                    {t('închisă {0}', daysAgo(r.closedAt!))}
                    {r.chosen ? (
                      <>
                        {r.chosen.split?.length ? (
                          <>
                            {t('· împărțită între')}
                            <b>{r.chosen.split.map((p) => p.supplier.name).join(' și ')}</b>
                          </>
                        ) : (
                          <>
                            {t('· ales')} <b>{r.chosen.supplier.name}</b>
                          </>
                        )}
                        {r.chosen.total == null ? '' : t(' · {0} lei', fmtNum(r.chosen.total))}
                      </>
                    ) : null}
                  </span>
                </span>
                <span className="count">
                  <b style={{ color: 'var(--ink)' }}>
                    {t('{0} din {1}', r.sends.filter((s) => s.hasOffer).length, r.sends.length)}
                  </b>
                  <br />
                  {t('oferte')}
                </span>
              </button>
            ))}
          </div>
        </section>
      ) : null}

      {editing ? (
        <SiteDialog site={site} toast={toast} onSaved={() => reload()} onClose={() => setEditing(false)} />
      ) : null}
    </div>
  );
}
